if (!customElements.get('box-builder')) {
  customElements.define(
    'box-builder',
    class BoxBuilder extends HTMLElement {
      constructor() {
        super();
        this.config = {};
        this.state = {
          giftBoxId: null,
          quantity: 0,
          flavorQuantities: {},
        };
      }

      connectedCallback() {
        const configEl = this.querySelector('[data-box-builder-config]');
        if (!configEl) return;

        this.config = JSON.parse(configEl.textContent);
        this.steps = Array.from(this.querySelectorAll('[data-box-builder-steps] [data-step]'));
        this.quantitySelect = this.querySelector('[data-quantity]');
        this.costEl = this.querySelector('[data-cost]');
        this.errorEl = this.querySelector('[data-error]');
        this.addBtn = this.querySelector('[data-action="add"]');
        this.spinner = this.querySelector('[data-spinner]');
        this.modal = this.querySelector('[data-limit-modal]');
        this.hintEl = this.querySelector('[data-selection-hint]');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.querySelectorAll('[data-gift-box]').forEach((input) => {
          input.addEventListener('change', () => this.selectGiftBox(input.value));
        });

        this.quantitySelect?.addEventListener('change', () => {
          this.selectQuantity(Number(this.quantitySelect.value) || 0);
        });

        this.querySelectorAll('[data-qty-change]').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            this.changeFlavorQuantity(button.dataset.flavorId, Number(button.dataset.qtyChange));
          });
        });

        this.querySelector('[data-close-modal]')?.addEventListener('click', () => this.closeModal());
        this.modal?.addEventListener('click', (event) => {
          if (event.target === this.modal) this.closeModal();
        });
        this.addBtn.addEventListener('click', () => this.addToCart());

        this.render();
      }

      get selectedBox() {
        return this.config.giftBoxes.find((box) => box.id === this.state.giftBoxId) || null;
      }

      get selectedQuantity() {
        return this.config.quantities.find((item) => item.count === this.state.quantity) || null;
      }

      get selectedFlavors() {
        return (this.config.flavors || [])
          .map((flavor) => ({
            ...flavor,
            quantity: Number(this.state.flavorQuantities[String(flavor.id)] || 0),
          }))
          .filter((flavor) => flavor.quantity > 0);
      }

      get totalFlavorQuantity() {
        return Object.values(this.state.flavorQuantities).reduce((sum, qty) => sum + Number(qty || 0), 0);
      }

      get matchedVariant() {
        return this.findGiftBoxVariant(this.selectedBox, this.selectedBox, this.state.quantity);
      }

      get isComplete() {
        const variant = this.matchedVariant;
        return Boolean(
          this.selectedBox &&
            this.state.quantity > 0 &&
            this.totalFlavorQuantity === this.state.quantity &&
            variant?.id &&
            variant.available
        );
      }

      tokens(value) {
        return String(value || '')
          .toLowerCase()
          .split(/[^a-z0-9]+/)
          .filter(Boolean);
      }

      hasToken(value, token) {
        return this.tokens(value).includes(String(token).toLowerCase());
      }

      variantFields(variant) {
        return [variant.title, variant.option1, variant.option2, variant.option3, ...(variant.options || [])].filter(
          Boolean
        );
      }

      normalizeCount(value) {
        const digits = String(value || '').trim().match(/^(\d+)$/);
        return digits ? digits[1] : '';
      }

      optionValues(variant) {
        return [variant.option1, variant.option2, variant.option3, ...(variant.options || [])]
          .filter((value) => value != null && String(value).trim() !== '')
          .map((value) => String(value).trim());
      }

      hasQuantity(variant, quantity) {
        const qty = this.normalizeCount(quantity);
        if (!qty) return false;
        return this.optionValues(variant).some((value) => {
          if (value.toLowerCase() === 'default title') return false;
          return this.normalizeCount(value) === qty || this.hasToken(value, qty);
        });
      }

      hasColor(value, color) {
        const colorToken = String(color || '').trim().toLowerCase();
        if (!colorToken) return false;
        return this.hasToken(value, colorToken);
      }

      variantHasColor(variant, color) {
        return this.variantFields(variant).some((field) => this.hasColor(field, color));
      }

      productIsColor(box, color) {
        return this.hasColor(box.label, color) || this.hasColor(box.productTitle, color) || this.hasColor(box.productHandle, color);
      }

      findCountOptionIndex(box) {
        const expected = new Set(['4', '6', '8', '10', '12', '14']);
        const options = box.options || [];
        return options.findIndex((option) =>
          (option.values || []).some((value) => expected.has(this.normalizeCount(value)))
        );
      }

      findGiftBoxVariant(product, giftBox, fragranceCount) {
        const box = giftBox || product;
        const qty = this.normalizeCount(fragranceCount);
        if (!box || !qty) return null;

        const countOptionIndex = this.findCountOptionIndex(box);
        const variants = box.variants || [];
        const qtyMatches = variants.filter((variant) => {
          if (countOptionIndex >= 0) {
            const optionValue = this.optionValues(variant)[countOptionIndex];
            return this.normalizeCount(optionValue) === qty || this.hasToken(optionValue, qty);
          }
          return this.hasQuantity(variant, qty);
        });

        if (!qtyMatches.length) return null;

        const color = box.label;
        const colorMatches = qtyMatches.filter((variant) => this.variantHasColor(variant, color));

        if (colorMatches.length === 1) return colorMatches[0];
        if (colorMatches.length > 1) {
          return (
            colorMatches.find((variant) =>
              this.optionValues(variant).some((value) => this.normalizeCount(value) === qty)
            ) || null
          );
        }

        if (this.productIsColor(box, color)) {
          if (qtyMatches.length === 1) return qtyMatches[0];
          return (
            qtyMatches.find((variant) =>
              this.optionValues(variant).some((value) => this.normalizeCount(value) === qty)
            ) || null
          );
        }

        return null;
      }

      findVariant(box, quantity) {
        return this.findGiftBoxVariant(box, box, quantity);
      }

      logVariantDebug(matchedVariant) {
        console.log({
          selectedGiftBox: this.selectedBox
            ? {
                label: this.selectedBox.label,
                productId: this.selectedBox.productId,
                productTitle: this.selectedBox.productTitle,
                productHandle: this.selectedBox.productHandle,
                options: this.selectedBox.options,
              }
            : null,
          selectedFragranceCount: this.state.quantity,
          product: this.selectedBox,
          variants: this.selectedBox?.variants || [],
          matchedVariant,
          matchReason: matchedVariant
            ? 'Matched fragrance-count option value on the selected gift box product.'
            : this.selectedBox && this.state.quantity
              ? `${this.selectedBox.label} does not currently have a ${this.state.quantity}-fragrance variant in Shopify.`
              : 'Gift box or fragrance count not selected.',
        });
      }

      selectGiftBox(id) {
        this.state.giftBoxId = id;
        this.render();
      }

      flavorQuantity(flavorId) {
        return Number(this.state.flavorQuantities[String(flavorId)] || 0);
      }

      selectQuantity(count) {
        this.state.quantity = count;
        this.render();
      }

      changeFlavorQuantity(flavorId, delta) {
        const id = String(flavorId);
        const current = this.flavorQuantity(id);
        const next = current + Number(delta || 0);

        if (next < 0) return;

        if (delta > 0) {
          if (!this.state.quantity || this.totalFlavorQuantity >= this.state.quantity) {
            this.showModal(this.config.labels.limitPopup);
            return;
          }
        }

        if (next === 0) {
          delete this.state.flavorQuantities[id];
        } else {
          this.state.flavorQuantities[id] = next;
        }

        this.render();
      }

      render() {
        const progress = this.state.giftBoxId ? (this.state.quantity ? (this.isComplete ? 3 : 2) : 1) : 1;

        this.steps.forEach((stepEl) => {
          const step = Number(stepEl.dataset.step);
          stepEl.classList.toggle('is-active', step === progress);
          stepEl.classList.toggle('is-complete', step < progress);
        });

        this.querySelectorAll('[data-gift-box]').forEach((input) => {
          const selected = input.value === this.state.giftBoxId;
          input.checked = selected;
          input.closest('.box-builder__box')?.classList.toggle('is-selected', selected);
        });

        if (this.quantitySelect) {
          this.quantitySelect.value = this.state.quantity ? String(this.state.quantity) : '';
        }

        this.querySelectorAll('[data-flavor-card]').forEach((card) => {
          const qty = this.flavorQuantity(card.dataset.flavorCard);
          card.classList.toggle('is-selected', qty > 0);
          const valueEl = card.querySelector('[data-qty-value]');
          if (valueEl) valueEl.textContent = String(qty);
          const minusBtn = card.querySelector('[data-qty-change="-1"]');
          if (minusBtn) minusBtn.disabled = qty === 0;
        });

        this.renderHint();
        this.renderCost();
        this.logVariantDebug(this.matchedVariant);
        this.addBtn.disabled = !this.isComplete;
      }

      renderHint() {
        if (!this.hintEl) return;

        const needed = this.state.quantity;
        const total = this.totalFlavorQuantity;
        const included = this.config.labels.included || 'Fragrances are included in the box price.';

        if (!needed) {
          this.hintEl.textContent =
            'Select the same number of fragrances as the option above. Fragrances are included in the box price.';
          return;
        }

        if (total > needed) {
          this.hintEl.textContent = `You currently have ${total} fragrances selected. Please reduce your selection to ${needed}.`;
          return;
        }

        if (total === needed) {
          this.hintEl.textContent = `${total} of ${needed} fragrances selected. ${included}`;
          return;
        }

        if (total === 0) {
          this.hintEl.textContent = `Select ${needed} fragrances. 0 of ${needed} selected. ${included}`;
          return;
        }

        const more = needed - total;
        this.hintEl.textContent = `${total} of ${needed} selected. Choose ${more} more fragrance${
          more === 1 ? '' : 's'
        }. ${included}`;
      }

      renderCost() {
        const variant = this.matchedVariant;

        if (this.state.quantity > 0 && this.totalFlavorQuantity > this.state.quantity) {
          this.costEl.textContent = variant?.price || '–';
          this.showError(
            `You currently have ${this.totalFlavorQuantity} fragrances selected. Please reduce your selection to ${this.state.quantity}.`
          );
          return;
        }

        if (this.selectedBox && this.state.quantity && !variant) {
          this.costEl.textContent = '–';
          this.showError(this.config.labels.unavailable);
          return;
        }

        if (variant && !variant.available) {
          this.costEl.textContent = variant.price || '–';
          this.showError(this.config.labels.unavailable);
          return;
        }

        this.clearError();
        this.costEl.textContent = this.selectedBox ? ('₹' + (500 + this.totalFlavorQuantity * 449).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })) : '₹';
      }

      addToCart() {
        const variant = this.matchedVariant;
        if (!this.selectedBox?.productId) {
          this.showError(this.config.labels.missingProduct);
          return;
        }

        if (!variant?.id || !variant.available) {
          this.showError(this.config.labels.unavailable);
          return;
        }

        if (this.totalFlavorQuantity < this.state.quantity) {
          const more = this.state.quantity - this.totalFlavorQuantity;
          this.showError(`Please select ${more} more fragrance${more === 1 ? '' : 's'}.`);
          return;
        }

        if (this.totalFlavorQuantity > this.state.quantity) {
          this.showError(this.config.labels.limitPopup);
          return;
        }

        if (!this.isComplete) {
          this.showError(this.config.labels.incomplete);
          return;
        }

        this.setLoading(true);
        this.clearError();

        const formData = new FormData();
        formData.append('id', String(variant.id));
        formData.append('quantity', '1');
        formData.append('properties[Gift Box]', this.selectedBox.label);
        formData.append('properties[Number of Fragrances]', String(this.state.quantity));
        this.selectedFlavors.forEach((flavor, index) => {
          formData.append(`properties[Fragrance ${index + 1}]`, `${flavor.title} × ${flavor.quantity}`);
        });

        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id).join(',')
          );
          formData.append('sections_url', window.location.pathname);
          if (typeof this.cart.setActiveElement === 'function') {
            this.cart.setActiveElement(document.activeElement);
          }
        }

        const addUrl = this.config.cartAddUrl || window.routes?.cart_add_url || '/cart/add.js';

        fetch(addUrl, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/javascript',
          },
          body: formData,
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message || this.config.labels.error);
              return;
            }

            if (this.cart && typeof this.cart.renderContents === 'function') {
              this.cart.classList.remove('is-empty');
              this.cart.renderContents(response);
              return;
            }

            window.location = this.config.cartUrl || window.routes?.cart_url || '/cart';
          })
          .catch(() => {
            this.showError(this.config.labels.error);
          })
          .finally(() => {
            this.setLoading(false);
          });
      }

      setLoading(isLoading) {
        this.addBtn.classList.toggle('is-loading', isLoading);
        if (this.spinner) this.spinner.hidden = !isLoading;
        this.addBtn.disabled = isLoading || !this.isComplete;
      }

      showModal(message) {
        const messageEl = this.modal?.querySelector('[data-limit-message]');
        if (messageEl) messageEl.textContent = message;
        if (this.modal?.showModal) {
          this.modal.showModal();
          return;
        }
        if (this.modal) this.modal.setAttribute('open', '');
      }

      closeModal() {
        if (this.modal?.close) {
          this.modal.close();
          return;
        }
        this.modal?.removeAttribute('open');
      }

      showError(message) {
        this.errorEl.hidden = false;
        this.errorEl.textContent = message;
      }

      clearError() {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }
    }
  );
}
